import { useState } from 'react'
import { Tooltip } from 'antd'
import { Table, TableSearch } from '@/components/ui'
import { StopOutlined, FilterOutlined } from '@ant-design/icons'
import { ZeroState } from '@/components/patterns'
import type { Attendee, Pagination, RegistrationField } from '@/pages/events/attendees/types'
import { useAttendeeColumns, buildQrLink } from '@/pages/events/attendees/hooks/useAttendeeColumns'
import QRPreviewModal from '@/components/QRPreviewModal'

interface RejectedCancelledTabProps {
  attendees: Attendee[]
  pagination: Pagination
  registrationFields: RegistrationField[]
  visibleColumns: Record<string, boolean>
  columnOrder: string[]
  onViewProfile: (id: number) => void
  onEditAttendee: (id: number) => void
  onDeleteAttendee: (id: number, name: string) => void
  onPrintBadge?: (attendee: Attendee) => void
  isPrintEnabled?: boolean
  printDisabledReason?: string
  printingAttendeeIds?: Set<number>
}

export default function RejectedCancelledTab({
  attendees,
  pagination,
  registrationFields,
  visibleColumns,
  columnOrder,
  onViewProfile,
  onEditAttendee,
  onDeleteAttendee,
  onPrintBadge,
  isPrintEnabled,
  printDisabledReason,
  printingAttendeeIds,
}: RejectedCancelledTabProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [qrRecord, setQrRecord] = useState<Attendee | null>(null)

  const handleSearch = () => {
    // TODO: Implement search functionality
    console.log('Search:', searchTerm)
  }

  const handleReset = () => {
    setSearchTerm('')
  }

  const handleFilter = () => {
    // TODO: Implement filter functionality
    console.log('Open filter')
  }

  const allColumns = useAttendeeColumns({
    registrationFields,
    onViewProfile,
    onEditAttendee,
    onDeleteAttendee,
    onShowQr: setQrRecord,
    onPrintBadge,
    isPrintEnabled,
    printDisabledReason,
    printingAttendeeIds,
    showDeleteAction: true,
  })

  const columnsByKey = Object.fromEntries(allColumns.map((col) => [col.key as string, col]))
  const orderedKeys = [
    ...columnOrder,
    ...Object.keys(columnsByKey).filter((key) => !columnOrder.includes(key) && key !== 'actions'),
  ]
  const columns = orderedKeys
    .map((key) => columnsByKey[key])
    .filter((col) => col)
    .filter((col) => (col.key === 'actions' ? true : visibleColumns[col.key as string]))

  const actionsCol = columnsByKey['actions']
  if (actionsCol && !columns.includes(actionsCol)) {
    columns.push(actionsCol)
  }

  return (
    <div className="min-h-[500px]">
      <div>
        {/* Search and Actions Bar */}
        <div className="flex items-center gap-3 mb-4">
          <TableSearch
            value={searchTerm}
            onChange={setSearchTerm}
            onSearch={handleSearch}
            onReset={handleReset}
            showFilter={false}
          />
          <Tooltip title="Filter">
            <button
              type="button"
              className="table-search-icon-btn"
              onClick={handleFilter}
              aria-label="Filter"
            >
              <FilterOutlined />
            </button>
          </Tooltip>
        </div>

        {/* Table */}
        {attendees.length === 0 ? (
          <ZeroState
            icon={<StopOutlined />}
            title="No Rejected or Cancelled Attendees"
            description="There are no rejected or cancelled registrations."
          />
        ) : (
          <Table
            className="ava-data-table"
            columns={columns}
            dataSource={attendees}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={{
              position: ['bottomLeft'],
              current: pagination.current_page,
              total: pagination.total_count,
              pageSize: pagination.per_page,
              showTotal: (total) => `Total ${total} items`,
              showSizeChanger: true,
              pageSizeOptions: ['10', '50', '100'],
              showQuickJumper: true,
            }}
          />
        )}
      </div>

      <QRPreviewModal
        open={!!qrRecord}
        onClose={() => setQrRecord(null)}
        title={qrRecord?.full_name ?? 'QR Code'}
        url={qrRecord?.qr_code ? buildQrLink(qrRecord.qr_code) : ''}
        value={qrRecord?.qr_code}
        subtitle={qrRecord?.qr_code}
        downloadFilename={qrRecord ? `${qrRecord.full_name}-qr` : 'qr-code'}
      />
    </div>
  )
}
